import {
  ISSUE_STATUS_LABELS,
  isStatusOnModePath,
  isTransitionAllowed,
} from '../types/issue';
import type { Issue, IssueStatus } from '../types/issue';
import {
  authorizingAttempts,
  canConfirmEfficacy,
  efficacyAttempts,
  evaluateIssueClosure,
  latestEfficacyAttempt,
  latestReopenAt,
  type CloseSubjectAction,
} from './efficacyCheck';
import { hasInvestigationFact, isCauseConfirmed } from './problemCause';

export type NextActionKind =
  | 'assign'
  | 'advance_status'
  | 'create_action'
  | 'root_cause'
  | 'validate'
  | 'close'
  | 'open_workspace';

export type ResponsibleRole = 'problem_owner' | 'verification_owner' | 'action_owner' | 'none';

export interface IssueNextAction {
  key: string;
  label: string;
  description: string;
  kind: NextActionKind;
  targetStatus?: IssueStatus;
  disabled: boolean;
  reason: string | null;
  responsibleRole: ResponsibleRole;
  legacy: boolean;
}

export interface ProblemNextActionOptions {
  directActions?: Array<CloseSubjectAction & { problem_id?: string | null }>;
  opportunityCount?: number;
  actor?: { id?: string | null; role?: string | null };
}

function withOpportunity(action: IssueNextAction, opportunityCount: number): IssueNextAction {
  if (opportunityCount <= 0 || action.kind === 'close') return action;
  const note = 'A oportunidade de melhoria não substitui esta ação.';
  if (action.description.includes(note)) return action;
  return { ...action, description: action.description ? `${action.description} ${note}` : note };
}

function actionOf(
  partial: Omit<IssueNextAction, 'disabled' | 'reason' | 'responsibleRole' | 'legacy' | 'description'> &
    Partial<Pick<IssueNextAction, 'disabled' | 'reason' | 'responsibleRole' | 'legacy' | 'description'>>,
): IssueNextAction {
  return {
    description: '',
    disabled: false,
    reason: null,
    responsibleRole: 'problem_owner',
    legacy: false,
    ...partial,
  };
}

function hasConfirmedCause(issue: Issue): boolean {
  return isCauseConfirmed(issue);
}

function stageFallback(issue: Issue): IssueNextAction {
  const quick = issue.mode === 'quick_correction';
  const legacy = issue.status !== 'closed' && !isStatusOnModePath(issue.mode, issue.status);
  if (legacy) {
    const canRecover = isTransitionAllowed(issue.mode, issue.status, 'action_plan');
    return actionOf({
      key: 'legacy_flow',
      label: 'Fluxo legado',
      description: `O registro está em ${ISSUE_STATUS_LABELS[issue.status] ?? issue.status}. O caminho da correção rápida é plano, execução, verificação e encerramento.`,
      kind: canRecover ? 'advance_status' : 'open_workspace',
      targetStatus: canRecover ? 'action_plan' : undefined,
      legacy: true,
    });
  }

  if (issue.status === 'validation' || issue.status === 'monitoring') {
    const storedReason = issue.metadata?.closure_reason?.trim() ?? '';
    const closure = evaluateIssueClosure({
      actions: [],
      attempts: efficacyAttempts(issue.metadata),
      closureReason: storedReason,
      metadata: issue.metadata,
    });
    const closeAllowed = closure.canClose && isTransitionAllowed(issue.mode, issue.status, 'closed');
    if (storedReason && closeAllowed) {
      return actionOf({
        key: 'close_problem',
        label: 'Encerrar problema',
        description: issue.status === 'monitoring'
          ? 'O motivo já está informado. O monitoramento pode encerrar.'
          : 'O motivo já está informado. A verificação pode encerrar.',
        kind: 'advance_status',
        targetStatus: 'closed',
        responsibleRole: 'none',
      });
    }
    if (!storedReason) {
      return actionOf({
        key: 'closure_reason',
        label: 'Informar o motivo do encerramento',
        description: 'Este problema não possui contramedida concluída. Informe o motivo pelo qual ele pode ser encerrado sem uma verificação de eficácia.',
        kind: 'advance_status',
        responsibleRole: 'problem_owner',
      });
    }
    return actionOf({
      key: 'close_problem',
      label: 'Encerrar problema',
      description: closure.reason ?? 'O motivo já está informado.',
      kind: 'advance_status',
      targetStatus: closeAllowed ? 'closed' : undefined,
      disabled: !closeAllowed,
      reason: closeAllowed ? null : (closure.reason ?? 'O encerramento ainda não está disponível nesta etapa.'),
      responsibleRole: 'none',
    });
  }

  if (!quick) {
    if (issue.status === 'open') {
      return actionOf({
        key: 'start_investigation',
        label: 'Iniciar investigação',
        description: 'O problema estruturado começa pela investigação.',
        kind: 'advance_status',
        targetStatus: 'investigation',
      });
    }
    if (issue.status === 'investigation' && !hasInvestigationFact(issue)) {
      return actionOf({
        key: 'continue_investigation',
        label: 'Continuar investigação',
        description: 'Registre ao menos um fato objetivo antes de tratar a causa.',
        kind: 'root_cause',
      });
    }
    if ((issue.status === 'investigation' || issue.status === 'root_cause') && !hasConfirmedCause(issue)) {
      return actionOf({
        key: issue.status === 'root_cause' ? 'register_cause' : 'confirm_cause',
        label: 'Confirmar a causa',
        description: 'Hipótese, 5 Porquês e Ishikawa não confirmam a causa. A confirmação é humana.',
        kind: 'root_cause',
      });
    }
    if (issue.status === 'root_cause' || issue.status === 'action_plan' || issue.status === 'investigation') {
      return actionOf({
        key: 'define_treatment',
        label: 'Definir tratamento',
        description: 'A causa não cria a contramedida. Alguém precisa registrá-la.',
        kind: 'create_action',
      });
    }
    if (issue.status === 'execution') {
      return actionOf({
        key: 'create_countermeasure',
        label: 'Criar contramedida',
        description: 'A execução não tem contramedida direta vinculada.',
        kind: 'create_action',
      });
    }
  }

  if (issue.status === 'open') {
    return actionOf({
      key: 'define_correction',
      label: 'Definir a correção',
      description: 'A correção rápida começa pela contramedida, sem análise estruturada.',
      kind: 'create_action',
    });
  }

  if (issue.status === 'action_plan' || issue.status === 'execution') {
    return actionOf({
      key: 'create_countermeasure',
      label: 'Criar contramedida',
      description: 'Ainda não há contramedida direta neste problema.',
      kind: 'create_action',
    });
  }

  return actionOf({
    key: 'open_case',
    label: 'Abrir o caso',
    description: 'A próxima etapa está no workspace.',
    kind: 'open_workspace',
  });
}

function verifyAction(issue: Issue, description: string, actor?: ProblemNextActionOptions['actor']): IssueNextAction {
  const verifierId = issue.metadata?.verification_owner_id;
  const base = actionOf({
    key: 'verify_efficacy',
    label: 'Verificar eficácia',
    description,
    kind: 'validate',
    responsibleRole: verifierId ? 'verification_owner' : 'problem_owner',
  });
  if (!actor) return base;
  const decision = canConfirmEfficacy({
    severity: issue.severity,
    verifierId,
    problemOwnerId: issue.assigned_to,
    actorId: actor.id,
    actorRole: actor.role,
  });
  if (decision.allowed === false) {
    return {
      ...base,
      disabled: true,
      reason: decision.reason,
      description: decision.reason,
    };
  }
  return base;
}

/**
 * Uma leitura de "o que fazer agora".
 * O encerramento usa evaluateIssueClosure. Esta função não recria a regra A/B/C.
 */
export function deriveProblemNextAction(issue: Issue, options: ProblemNextActionOptions = {}): IssueNextAction {
  const opportunityCount = options.opportunityCount ?? 0;

  if (issue.status === 'closed') {
    return actionOf({
      key: 'closed',
      label: 'Problema encerrado',
      description: 'Não há próxima ação operacional. A reabertura é administrativa e pede motivo.',
      kind: 'close',
      responsibleRole: 'none',
    });
  }

  if (options.directActions) {
    const actions = options.directActions.filter((item) => !item.problem_id || item.problem_id === issue.id);
    const blocked = actions.filter((item) => item.status === 'blocked');
    const running = actions.filter((item) => item.status === 'in_progress');
    const pending = actions.filter((item) => item.status === 'pending');
    const completed = actions.filter((item) => item.status === 'completed');

    if (blocked.length > 0) {
      return withOpportunity(actionOf({
        key: 'resolve_block',
        label: 'Resolver bloqueio',
        description: blocked.length === 1
          ? 'Existe 1 contramedida bloqueada. O desbloqueio é feito na própria ação.'
          : `Existem ${blocked.length} contramedidas bloqueadas. O desbloqueio é feito na própria ação.`,
        kind: 'open_workspace',
        responsibleRole: 'action_owner',
      }), opportunityCount);
    }
    if (running.length > 0) {
      return withOpportunity(actionOf({
        key: 'complete_countermeasure',
        label: 'Concluir contramedida',
        description: 'A execução continua na contramedida em andamento.',
        kind: 'open_workspace',
        responsibleRole: 'action_owner',
      }), opportunityCount);
    }
    if (pending.length > 0) {
      return withOpportunity(actionOf({
        key: 'start_countermeasure',
        label: 'Executar contramedida',
        description: 'Há contramedida pendente. Concluir a ação não encerra o problema.',
        kind: 'open_workspace',
        responsibleRole: 'action_owner',
      }), opportunityCount);
    }

    if (completed.length > 0) {
      const attempts = efficacyAttempts(issue.metadata);
      const authorizing = authorizingAttempts(attempts, latestReopenAt(issue.metadata));
      const latest = latestEfficacyAttempt(authorizing);
      if (latest?.result === 'ineffective') {
        return withOpportunity(actionOf({
          key: 'revise_treatment',
          label: 'Revisar o tratamento',
          description: 'A última verificação não foi eficaz. Registre uma nova contramedida. A tentativa anterior permanece no histórico.',
          kind: 'create_action',
        }), opportunityCount);
      }
      const closure = evaluateIssueClosure({
        actions: completed,
        attempts,
        closureReason: issue.metadata?.closure_reason,
        metadata: issue.metadata,
      });
      if (!closure.canClose) {
        return withOpportunity(
          verifyAction(issue, closure.reason ?? 'As contramedidas concluídas ainda não autorizam o encerramento.', options.actor),
          opportunityCount,
        );
      }
      if (isTransitionAllowed(issue.mode, issue.status, 'closed')) {
        return withOpportunity(actionOf({
          key: 'close_problem',
          label: 'Encerrar problema',
          description: 'A verificação cobre as contramedidas concluídas.',
          kind: 'advance_status',
          targetStatus: 'closed',
          responsibleRole: 'none',
        }), opportunityCount);
      }
      return withOpportunity(actionOf({
        key: 'advance_to_verification',
        label: 'Ir para verificação',
        description: 'A eficácia está registrada. O encerramento só aparece na etapa de verificação.',
        kind: 'advance_status',
        targetStatus: 'validation',
      }), opportunityCount);
    }
  } else if ((issue.linked_actions_count ?? 0) > 0) {
    if (issue.efficacy_ready) {
      return withOpportunity(
        verifyAction(issue, 'Há contramedida concluída sem eficácia confirmada.', options.actor),
        opportunityCount,
      );
    }
    return withOpportunity(actionOf({
      key: 'follow_countermeasure',
      label: 'Acompanhar contramedida',
      description: 'A lista não distingue pendente, andamento e bloqueio. Abra a ação.',
      kind: 'open_workspace',
      responsibleRole: 'action_owner',
    }), opportunityCount);
  }

  return withOpportunity(stageFallback(issue), opportunityCount);
}

export function getIssueNextAction(issue: Issue, options?: ProblemNextActionOptions): IssueNextAction {
  return deriveProblemNextAction(issue, options);
}
